import { Bell, X } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { useAskV2ReviewEmailNotificationCta } from "./useAskV2ReviewEmailNotificationCta";

type AskV2ReviewEmailNotificationBannerProps = {
  chatId: string;
  reviewInProgress: boolean;
};

export function AskV2ReviewEmailNotificationBanner({
  chatId,
  reviewInProgress,
}: AskV2ReviewEmailNotificationBannerProps) {
  const { t } = useTranslation("ui");
  const { visible, enabling, enable, dismiss } =
    useAskV2ReviewEmailNotificationCta(chatId, reviewInProgress);

  if (!visible) return null;

  return (
    <div
      data-testid="ask-v2-review-email-notification-banner"
      role="region"
      aria-label={t("askV2.transport.reviewEmailNotificationBannerAriaLabel")}
      className="rounded-xl border border-puntt-accent-6 bg-puntt-accent-2 px-3 py-2"
    >
      <Flex gap="2" align="start">
        <Bell
          size={16}
          weight="fill"
          className="mt-0.5 shrink-0 text-puntt-accent-11"
          aria-hidden="true"
        />
        <Flex direction="column" gap="2" className="min-w-0 flex-1">
          <Text size="2" className="text-puntt-neutral-gray-12">
            {t("askV2.transport.reviewEmailNotificationBannerMessage")}
          </Text>
          <Flex gap="2" align="center">
            <Button
              size="1"
              data-testid="ask-v2-review-email-notification-banner-enable"
              disabled={enabling}
              onClick={() => {
                void enable();
              }}
            >
              {t("askV2.transport.reviewEmailNotificationBannerEnable")}
            </Button>
            <Button
              size="1"
              variant="ghost"
              color="gray"
              data-testid="ask-v2-review-email-notification-banner-dismiss"
              disabled={enabling}
              onClick={dismiss}
            >
              {t("askV2.transport.reviewEmailNotificationBannerDismiss")}
            </Button>
          </Flex>
        </Flex>
        <button
          type="button"
          data-testid="ask-v2-review-email-notification-banner-close"
          aria-label={t(
            "askV2.transport.reviewEmailNotificationBannerCloseAriaLabel",
          )}
          disabled={enabling}
          onClick={dismiss}
          className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-200 hover:bg-puntt-neutral-gray-4 disabled:opacity-50"
        >
          <X size={14} className="text-puntt-neutral-gray-11" aria-hidden />
        </button>
      </Flex>
    </div>
  );
}
